import React from 'react';

const CompanySettings = ({ 
  formData, 
  handleChange, 
  handleLogoChange, 
  handleLogoClick, 
  logoPreview, 
  fileInputRef 
}) => {
  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h2 className="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-3 mb-6">
        Company Settings
      </h2>
      
      {/* Company Name */}
      <div className="mb-6">
        <label 
          htmlFor="company_name" 
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Company Name 
        </label>
        <input
          type="text"
          id="company_name"
          name="company_name"
          value={formData.company_name}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded-md"
          placeholder="Your Company Name"
        />
        <p className="mt-1 text-sm text-gray-500">
          Your company name may be displayed in public evaluations pages.
        </p>
      </div>
      
      {/* Company Logo */}
      <div className="mb-6">
        <label 
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Company Logo 
        </label>
        <div className="flex items-center">
          <button
            type="button"
            onClick={handleLogoClick}
            className="px-4 py-2 bg-gray-200 text-gray-700 font-semibold rounded hover:bg-gray-300 transition-colors"
          >
            Choose Logo
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleLogoChange}
            className="hidden"
            accept="image/*"
          />
          <span className="ml-3 text-sm text-gray-500">
            {formData.company_logo instanceof File ? formData.company_logo.name : 'No file chosen'}
          </span>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Your company logo may be displayed in public evaluations pages.
        </p>
        
        {/* Logo Preview */}
        {logoPreview && (
          <div className="mt-3">
            <p className="text-sm font-medium text-gray-700 mb-1">Preview:</p>
            <img 
              src={logoPreview} 
              alt="Company Logo Preview" 
              className="max-w-xs max-h-32 border border-gray-300 rounded-md"
            />
          </div>
        )}
      </div>
      
      {/* Evaluation Completed Content */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-1">
          <label 
            htmlFor="evaluation_completed_content" 
            className="block text-sm font-medium text-gray-700"
          >
            Evaluation Completed Content 
          </label>
          <div className="relative group">
            <button 
              type="button"
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
              aria-label="Show markup help"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </button>
            <div className="absolute right-0 w-96 p-4 mt-2 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 border border-gray-200 text-sm">
              <h4 className="font-semibold text-gray-800 mb-2">Supported markup format:</h4>
              <div className="space-y-2 text-gray-600">
                <p><code className="bg-gray-100 px-1 py-0.5 rounded"># Heading 1</code> - Creates an &lt;h1&gt; element</p>
                <p><code className="bg-gray-100 px-1 py-0.5 rounded">## Heading 2</code> - Creates an &lt;h2&gt; element</p>
                <p><code className="bg-gray-100 px-1 py-0.5 rounded">### Heading 3</code> - Creates an &lt;h3&gt; element</p>
                
                <p><code className="bg-gray-100 px-1 py-0.5 rounded">- Bullet point</code> - Creates a bullet list item</p>
                <p><code className="bg-gray-100 px-1 py-0.5 rounded">* Alternative bullet</code> - Also creates a bullet list item</p>
                <p><code className="bg-gray-100 px-1 py-0.5 rounded">- [sm] Small text bullet</code> - Creates a bullet with small text</p>
                <p><code className="bg-gray-100 px-1 py-0.5 rounded">- [md] Medium text bullet</code> - Creates a bullet with medium text</p>
                <p><code className="bg-gray-100 px-1 py-0.5 rounded">- [lg] Large text bullet</code> - Creates a bullet with large text</p>
                
                <p><code className="bg-gray-100 px-1 py-0.5 rounded">1. Numbered item</code> - Creates a numbered list item</p>
                <p><code className="bg-gray-100 px-1 py-0.5 rounded">2. Another item</code> - Creates another numbered list item</p>
                <p><code className="bg-gray-100 px-1 py-0.5 rounded">1. [sm] Small numbered item</code> - Creates a numbered item with small text</p>
                <p><code className="bg-gray-100 px-1 py-0.5 rounded">2. [md] Medium numbered item</code> - Creates a numbered item with medium text</p>
                <p><code className="bg-gray-100 px-1 py-0.5 rounded">3. [lg] Large numbered item</code> - Creates a numbered item with large text</p>
                
                <p>Regular text - Creates a paragraph</p>
                
                <p className="italic">Each line is processed separately, so lists need to be created with consecutive lines using the same format.</p>
                
                <div className="mt-2">
                  <p className="font-medium">Example:</p>
                  <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto">
# Welcome to Your Results

## What happens next?

- Your evaluation will be reviewed by our team
- You will receive feedback within 3-5 business days

1. Complete any additional steps
2. Prepare for the next round if selected
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
        <textarea
          id="evaluation_completed_content"
          name="evaluation_completed_content"
          value={formData.evaluation_completed_content}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded-md min-h-[200px]"
          placeholder="Enter content to show after evaluation completion..."
        />
        <p className="mt-1 text-sm text-gray-500">
          This content will appear at the end of an evaluation and is used to explain next steps to talent.
          Hover over the info icon above for supported markup format.
        </p>
      </div>
    </div>
  );
};

export default CompanySettings;
